import { forwardRef, HTMLProps, ReactNode } from 'react';

import dynamic from 'next/dynamic';
import { LinkProps } from 'next/link';
import { tv } from 'tailwind-variants';

import cn from '@/helpers/utils/classNames';

const Link = dynamic(() => import('next/link'));

type CustomButton = Omit<HTMLProps<HTMLButtonElement>, 'type' | 'as'>;

export interface RawButtonProps extends CustomButton {
  scheme: 'primary' | 'secondary' | 'error' | 'text' | 'danger';
  variant: 'outlined' | 'filled';
  type: 'button' | 'submit';
  children: ReactNode;
  btnSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  target?: '_blank' | '_parent' | ' _self' | '_top';
  prefetch?: boolean;
}

export type ButtonProps = RawButtonProps &
  (
    | {
        as?: 'Link';
        href: Pick<LinkProps, 'href'> | string;
        onClick?: () => void;
      }
    | {
        as?: 'button' | 'a';
      }
  );

export const buttonStyles = tv({
  base: 'v2-border v2-whitespace-nowrap v2-transition-colors v2-font-sweet-sans-pro v2-font-medium disabled:v2-cursor-not-allowed disabled:v2-active:!v2-shadow-none v2-p-1 v2-px-4 disabled:v2-text-gray-400 v2-text-base hover:v2-transition-colors v2-rounded-xs',
  variants: {
    primary: {
      filled:
        'v2-bg-primary-600 v2-border-primary-600 v2-shadow-xs v2-text-white hover:v2-bg-primary-700 hover:v2-border-primary-700 active:v2-shadow-round disabled:v2-bg-gray-100 disabled:v2-border-gray-200',
      outlined:
        'v2-bg-white v2-border-primary v2-shadow-xs v2-text-primary hover:v2-text-primary-800 hover:v2-bg-primary-50 active:v2-bg-white disabled:v2-bg-white disabled:v2-border-gray-200 active:v2-shadow-round',
    },
    secondary: {
      filled:
        'v2-bg-gray-100 v2-border-gray-300 v2-shadow-xs v2-text-gray-900 active:v2-shadow-xs active:v2-shadow-gray-400 disabled:hover:v2-bg-white',
      outlined: 'v2-bg-white v2-border-gray-300 v2-shadow-xs v2-text-gray-900 disabled:hover:v2-bg-white',
    },
    error: {
      filled:
        'v2-bg-error v2-border-error v2-shadow-xs v2-text-white active:v2-shadow-xs active:v2-shadow-error-400 disabled:v2-bg-gray-100  disabled:v2-border-gray-200',
      outlined:
        'v2-bg-white v2-border-error v2-shadow-xs v2-text-error disabled:v2-bg-gray-100  disabled:v2-border-gray-200',
    },
    danger: {
      filled:
        'v2-bg-error v2-border-error v2-shadow-xs v2-text-white active:v2-shadow-xs active:v2-shadow-error-400 disabled:v2-bg-gray-100  disabled:v2-border-gray-200',
      outlined:
        'v2-bg-white v2-border-error v2-shadow-xs v2-text-[#B42318] disabled:v2-bg-gray-100  disabled:v2-border-gray-200',
    },
    text: {
      filled: 'v2-bg-white v2-border-transparent v2-text-gray-700 hover:v2-bg-gray-50 disabled:v2-text-gray-400',
      outlined: 'v2-bg-white v2-border-transparent v2-text-gray-700 hover:v2-bg-gray-50',
    },
  },
});

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const {
    scheme,
    as,
    type,
    variant,
    disabled,
    className,
    href,
    btnSize,
    onClick,
    children,
    target,
    prefetch,
    ...restProps
  } = props;
  const sizes = {
    xs: 'v2-py-2 v2-px-3',
    sm: 'v2-py-[10px] v2-px-[14px]',
    md: 'v2-py-[10px] v2-px-4',
    lg: 'v2-py-3 v2-px-[18px]',
    xl: 'v2-py-4 v2-px-[22px]',
  };

  if (as === 'a')
    return (
      <a
        href={href}
        className={cn(
          buttonStyles({ [scheme]: variant }),
          {
            'v2-cursor-not-allowed v2-font-sweet-sans-pro v2-border-transparent v2-bg-grayscale-60 v2-text-white hover:v2-border-transparent hover:v2-bg-grayscale-70 hover:v2-text-white focus:v2-bg-grayscale-70 focus:v2-text-white':
              disabled,
          },
          className
        )}
      >
        {children}
      </a>
    );

  return as === 'Link' && href ? (
    <Link
      href={disabled ? '#' : href}
      target={target}
      prefetch={!disabled && prefetch}
      type={type || 'button'}
      className={cn(
        'v2-inline-block',
        buttonStyles({ [scheme]: variant }),
        sizes[btnSize],
        {
          'v2-cursor-not-allowed v2-opacity-50 v2-pointer-events-none': disabled,
        },
        className
      )}
      onClick={disabled ? e => e.preventDefault() : onClick}
    >
      {children}
    </Link>
  ) : (
    <button
      ref={ref}
      type={type || 'button'}
      className={cn(buttonStyles({ [scheme]: variant }), sizes[btnSize], className, 'active:disabled:!v2-shadow-none')}
      disabled={!!disabled}
      onClick={onClick}
      {...restProps}
    >
      {children}
    </button>
  );
});

export default Button;
